'use strict';

const React = require('react');
const useEffect = React.useEffect;
const useState = React.useState;
const useRef = React.useRef;
const PropTypes = require('prop-types');
const debug = require('debug');

const imageConversion = require('image-conversion');
const { IconButton } = require('@material-ui/core');
const data = require('emoji-mart/data/apple.json');
const Picker = require('emoji-mart/dist-modern/components/picker/nimble-picker').default;
const computedStyleToInlineStyle = require('computed-style-to-inline-style');
const xss = require('xss');

const {
    CancelOutlined: CloseIcon,
    Create: CreateIcon
} = require('@material-ui/icons');
const OldMessage = require('./Chat/OldMessage');

const DEBUG = debug('blinkrtc:ConferenceChatEditor');


const ConferenceChatEditor = (props) => {
    const [name, setName] = useState('');
    const [picker, setPicker] = useState(false);
    const [type, _setType] = useState('text/plain');
    const [timer, setTimer] = useState(null);

    const editor = useRef(null);
    const typeRef = useRef('text/plain');

    useEffect(() => {
        return () => clearTimeout(timer);
    }, [props.onSubmit, timer]);

    const setType = type => {
        typeRef.current = type
        _setType(type);
    }

    useEffect(() => {
        if (props.editMessage) {
            if (props.editMessage.contentType === 'application/sylk-file-transfer') {
                const metadata = props.editMessage.metadata?.find(m => m.action === 'label');
                setName(metadata?.value || '');
                editor.current.blur();
                editor.current.focus();
                return
            }
            setName(props.editMessage.content);
            editor.current.blur();
            editor.current.focus();
        } else {
            setName('');
        }
    }, [props.editMessage]);

    const isCaptionEditor = props.type === 'caption';

    const setMessage = () => {
        if (props.editMessage?.contentType === 'application/sylk-file-transfer') {
            const metadata = props.editMessage.metadata?.find(m => m.action === 'label');
            return { __html: metadata?.value || '' }
        }
        return {
            __html: props.editMessage && props.editMessage.content
        }
    }

    const togglePicker = () => {
        setPicker(!picker);
        props.scroll();
    }

    const toggleRecording = (event) => {
        props.toggleRecordVoiceMessage(event.currentTarget)
    }

    const handleInput = (e) => {
        DEBUG('Paste detected');
        let found = false;
        let target = e.currentTarget;
        const imageItems = [];
        const fileItems = [];
        for (const item of e.clipboardData.items) {
            if (!isCaptionEditor && item.type.match(/image.*/)) {
                imageItems.push(item);
            } else if (item.kind === 'file') {
                fileItems.push(item);
            }
        }

        if (fileItems.length > 0) {
            const dt = new DataTransfer();
            [...fileItems, ...imageItems].forEach((item) => dt.items.add(item.getAsFile()));
            props.upload({ target: { files: dt.files } });
            found = true;
        } else if (imageItems.length > 0) {
            DEBUG(`${imageItems.length} image(s) pasted`);
            e.preventDefault();

            const processImage = (item) =>
                new Promise((resolve) => {
                    const file = item.getAsFile();
                    DEBUG(file);
                    imageConversion.compress(file, { scale: 0.5 }).then((res) => {
                        const reader = new FileReader();
                        reader.onload = (evt) => {
                            const base64 = btoa(
                                new Uint8Array(evt.target.result)
                                    .reduce((data, byte) => data + String.fromCharCode(byte), '')
                            );
                            const image = `data:${file.type};base64,${base64}`;
                            const imageTag = `<div class="file-box" onclick="window.getSelection().selectAllChildren(this)" style="background-image: url('${image}')">${file.name}</div>`;
                            resolve({ name: base64, type: file.type, tag: imageTag, size: res.size, file });
                        };
                        reader.readAsArrayBuffer(res);
                    });
                });

            Promise.all(imageItems.map(processImage)).then((results) => {
                const totalSize = results.reduce((sum, r) => sum + r.size, 0);

                if (totalSize >= 12 * 1024) {
                    const dt = new DataTransfer();
                    results.forEach((r) => dt.items.add(r.file));
                    props.upload({ target: { files: dt.files } });
                    return;
                }

                setName(results.map((r) => r.name).join(','));
                setType(results[results.length - 1].type);

                const combinedHTML = results.map((r) => r.tag).join('');

                const selection = document.getSelection();
                if (target.innerHTML === '' || !selection.rangeCount) {
                    target.innerHTML = combinedHTML;
                } else {
                    selection.deleteFromDocument();
                    const template = document.createElement('template');
                    template.innerHTML = combinedHTML;
                    selection.getRangeAt(0).insertNode(template.content.cloneNode(true));
                }
                target.focus();
            });

            found = true;
        }

        if (!found) {
            let data = e.clipboardData.getData('text/html');
            if (data === '') {
                DEBUG('No HTML data on clipboard');
                data = e.clipboardData.getData('text/plain');
                const cleanData = data;
                if (target.innerHTML === '') {
                    e.preventDefault();
                    target.innerText = cleanData;
                } else {
                    e.preventDefault();
                    const selection = document.getSelection();
                    selection.deleteFromDocument();
                    const div = document.createTextNode(cleanData);
                    selection.getRangeAt(0).insertNode(div);
                }
            } else {
                DEBUG('HTML data on clipboard, content type will change');
                e.preventDefault();
                let cleanData = data;
                if (isCaptionEditor) {
                    setType('text/plain');
                    const div = document.createElement('div');
                    div.innerHTML = cleanData;
                    div.querySelectorAll('a').forEach(el => el.replaceWith(el.href));
                    div.querySelectorAll('br').forEach(el => el.replaceWith('\n'));
                    div.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6').forEach(el => {
                        el.after(document.createTextNode('\n'));
                    });
                    cleanData = div.textContent.trim();
                    cleanData = xss(cleanData, {
                        whiteList: [], // empty, means filter out all tags
                        stripIgnoreTag: true, // filter out all HTML not in the whitelist
                        stripIgnoreTagBody: ['script', 'style']  // the script tag is a special case, we need
                        // to filter out its content
                    }).replace(/&nbsp;/g, ' ');
                } else {
                    setType('text/html');
                    cleanData = data.replace(/\n|\t/g, '');
                }
                if (target.innerHTML === '') {
                    if (isCaptionEditor) {
                        target.innerText = cleanData;
                    } else {
                        target.innerHTML = cleanData;
                    }
                } else {
                    const selection = document.getSelection();
                    selection.deleteFromDocument();
                    const div = document.createElement('template');
                    div.innerHTML = cleanData;
                    selection.getRangeAt(0).insertNode(div.content.cloneNode(true));
                }
                if (!isCaptionEditor) {
                    computedStyleToInlineStyle(target, {
                        recursive: true,
                        properties: [
                            'font-family',
                            'font-size',
                            'margin',
                            'color',
                            'background-color'
                        ]
                    });
                }
            }
            setTimeout(() => {
                if (type === 'text/html' || typeRef.current == 'text/html') {
                    setName(`${target.innerHTML}`);
                } else {
                    setName(`${target.innerText}`);
                }
                editor.current.blur();
                editor.current.focus();
            }, 50);
        }
    }

    const sendMessage = () => {
        if (timer !== null) {
            clearTimeout(timer);
        }
        if ((name !== '' && name !== '\n') || isCaptionEditor) {
            if (type === 'text/html') {
                DEBUG('Sending HTML content');
                props.onSubmit(name, type);
            } else if (type.startsWith('image/')) {
                DEBUG('Sending image');
                props.onSubmit(name, type)
            } else {
                DEBUG('Sending text content');
                props.onSubmit(name)
            }
        } else {
            return;
        }
        if (picker) {
            setPicker(false);
        }
        setType('text/plain');
        while (editor.current.firstChild) editor.current.removeChild(editor.current.firstChild);
        setName('');
    }

    const onKeyDown = (event) => {
        let target = event.currentTarget;
        switch (event.which) {
            case 27:
                // ESC
                DEBUG('Esc pressed, clear input or close emoji');
                if (picker) {
                    setPicker(false);
                } else {
                    setType('text/plain');
                    while (target.firstChild) target.removeChild(target.firstChild);
                    setName('');

                    if (props.editMessage) {
                        props.cancelEdit()
                    }
                }
                break;
            case 13:
                // Enter
                DEBUG('ENTER pressed');
                if (!event.shiftKey) {
                    event.preventDefault();
                    event.stopPropagation();
                    sendMessage();
                }
                break;
            case 91:
                break;
            case 8:
                // Backspace in case of image, delete all
                if (type.startsWith('image/')) {
                    event.preventDefault();
                    setName('');
                    setType('text/plain');
                    while (target.firstChild) target.removeChild(target.firstChild);
                    break;
                }
            default:
                if (!(event.ctrlKey || event.which === 224)) {
                    setTimeout(() => {
                        if (target.innerHTML === '<br>') {
                            DEBUG('Editor is empty');
                            while (target.firstChild) target.removeChild(target.firstChild);
                            if (timer !== null) {
                                DEBUG('__reset__');
                                clearTimeout(timer);
                                setTimer(null);
                                props.onTyping('idle');
                            }
                        } else if (timer === null) {
                            setTimer(
                                setTimeout(() => {
                                    DEBUG('__idle__');
                                    props.onTyping('idle');
                                    setTimer(null);
                                }, 10000)
                            );
                            DEBUG('__composing__');
                            props.onTyping('active');
                        }
                        if (type === 'text/html') {
                            setName(target.innerHTML);
                        } else {
                            setName(target.innerText);
                        }
                    }, 5);
                }
                break;
        }
    };

    const addEmoji = (emoji) => {
        editor.current.innerText = editor.current.innerText + `${emoji.native}`;
        setName(editor.current.innerText);
        editor.current.focus();
    }

    const moveCursorToEnd = (el) => {
        if (el.innerText && document.createRange && !type.startsWith('image/')) {
            window.setTimeout(() => {
                let selection = document.getSelection();
                let range = document.createRange();
                range.selectNodeContents(el);
                range.collapse(false);

                selection.removeAllRanges();
                selection.addRange(range);
            }, 1);
        }
    }

    return (
        <div style={{ margin: '0 -15px' }}>
            {picker ? <div style={{ minHeight: '360px', flex: '0 0 auto', order: 2 }} /> : ''}
            <div className="top-editor-wrapper">
                {props.editMessage &&
                    <div className="editor-row">
                        <div style={{ flex: 'auto', marginLeft: '70px', marginRight: '23px', marginBottom: '8px', marginTop: '-10px' }}><CreateIcon style={{ fontSize: '24px', color: '#31708f' }} /></div>
                        <div className="old-message">
                            <OldMessage
                                message={props.editMessage}
                            />
                        </div>
                        <IconButton
                            onClick={() => props.cancelEdit()} variant="text" title="Cancel edit"
                            disableFocusRipple={true}
                            disableRipple={true}
                            style={{ marginTop: '-10px', marginLeft: '-10px', marginRight: '4px', marginBottom: '8px', padding: '10px', fontSize: 'inherit' }}
                        >
                            <CloseIcon style={{ fontSize: '24px', margin: '-5px' }} />
                        </IconButton>
                    </div>
                }
                <div className="editor-row">
                    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%' }}>
                        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%' }}>
                            {picker ?
                                <Picker
                                    set="apple"
                                    data={data}
                                    showPreview={false}
                                    sheetSize={32}
                                    showSkinTones={false}
                                    onSelect={addEmoji}
                                    style={{ position: 'relative', width: '100%' }}
                                    backgroundImageFn={() =>
                                        'assets/images/32.png'}
                                /> : ''}
                        </div>
                    </div>
                    {props.upload && [
                        <input
                            style={{ display: 'none' }}
                            id="outlined-button-file"
                            multiple
                            type="file"
                            onChange={props.upload}
                            key="1"
                        />,
                        <label key="shareFiles" className="upload-button-label" htmlFor="outlined-button-file">
                            <div className="upload-button">
                                <i className="fa fa-plus fa-2x" />
                            </div>
                        </label>]
                    }
                    {props.enableVoiceMessage &&
                        <div className="upload-button" onClick={toggleRecording}>
                            <i className="fa fa-microphone fa-2x" />
                        </div>
                    }

                    <div className={`emoji-button ${props.upload && 'padding-fixed'}`} onClick={togglePicker}>
                        <i className="fa fa-smile-o fa-2x" />
                    </div>
                    <div className="editor-wrapper">
                        <div className="editor-inner-wrapper">
                            <div className="pre-editor" style={{ visibility: name.length !== 0 ? 'hidden' : 'visible' }}>
                                Type a {isCaptionEditor ? 'caption' : 'message'}
                            </div>
                            <div className="editor"
                                contentEditable="true"
                                onPaste={handleInput}
                                onKeyDown={onKeyDown}
                                onFocus={(e) => { moveCursorToEnd(e.target); props.focus() }}
                                onBlur={() => props.focus()}
                                ref={editor}
                                dangerouslySetInnerHTML={setMessage()}
                            ></div>
                        </div>
                    </div>
                    {(name.length !== 0 || isCaptionEditor) &&
                        <IconButton
                            onClick={() => sendMessage()} variant="text" title="Send"
                            disableFocusRipple={true}
                            disableRipple={true}
                            color="primary"
                            style={{ marginLeft: '-10px', marginRight: '4px', padding: '10px', fontSize: 'inherit' }}
                        >
                            <i className="fa fa-paper-plane" aria-hidden="true" ></i>
                        </IconButton>
                    }
                </div>
            </div>
        </div >
    );
};

ConferenceChatEditor.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    onTyping: PropTypes.func.isRequired,
    scroll: PropTypes.func.isRequired,
    focus: PropTypes.func.isRequired,
    upload: PropTypes.func,
    enableVoiceMessage: PropTypes.bool,
    toggleRecordVoiceMessage: PropTypes.func,
    editMessage: PropTypes.any,
    cancelEdit: PropTypes.func,
    type: PropTypes.string
};


module.exports = ConferenceChatEditor;
